import { CloudUpload } from '@mui/icons-material';
import {
  Dialog,
  Box,
  DialogTitle,
  DialogContent,
  ThemeProvider,
  Button,
  DialogActions,
} from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers-pro';
import { customDialogImportTheme } from 'assets/styles/themes/Dialog/generic_dialog_import';
import {
  customButtonTheme,
  customFileButtonTheme,
  customRedButtonTheme,
} from 'assets/styles/themes/generic_button_mui';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { importCultureMappingData, importGroundTypeMappingData } from 'services/API/Cooperative';
import { LoadingContext, MappingImportResponseContext } from 'utils/context';
import { displayErrorMessage, displayMessage, formatFileSize } from 'utils/tools_functions';
import { AxiosError } from 'axios';

const ImportMappingFileModal = ({
  selectedMappingType,
  cooperativeId,
  open,
  setModalOpen,
  setOpenImportButton,
  refreshDataAfterNewFileImport,
  from = 'mappingCulture',
}) => {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const { setCustomText } = useContext(LoadingContext);
  const { setCultureMappingImportResponse, setGroundTypeMappingImportResponse } = useContext(
    MappingImportResponseContext,
  );

  const setMappingImportResponse =
    from === 'mappingCulture'
      ? setCultureMappingImportResponse
      : setGroundTypeMappingImportResponse;

  const importMappingDataFromFile = async () => {
    setModalOpen(false);
    setOpenImportButton(false);
    setCustomText(displayMessage('IMPORT_MAPPING'));
    let formData = new FormData();
    formData.append('file', file);
    try {
      let response = null;
      if (from === 'mappingCulture') {
        response = await importCultureMappingData(selectedMappingType, cooperativeId, formData);
      } else {
        response = await importGroundTypeMappingData(selectedMappingType, cooperativeId, formData);
      }
      toast.success(t('success.mappingImported'));

      let warningInfo = [];
      response?.data?.data?.importedEntities.forEach((importedEntity) => {
        if ('warningInfo' in importedEntity) {
          warningInfo.push(importedEntity.warningInfo);
        }
      });
      setMappingImportResponse({
        skippedLines: response?.data?.data?.skippedLines,
        warningInfo: warningInfo,
        hasWarning: warningInfo.length > 0 || response?.data?.data?.skippedLines.length > 0,
        mappingType: selectedMappingType,
      });
      refreshDataAfterNewFileImport(selectedMappingType, response?.data?.data?.importedEntities);
    } catch (error) {
      setMappingImportResponse({
        skippedLines: [],
        warningInfo: [],
        hasWarning: false,
        mappingType: null,
      });
      if (error instanceof AxiosError) {
        toast.error(error?.response?.data?.message ?? displayErrorMessage('ERR_IMPORT_CSV'));
      } else {
        toast.error(displayErrorMessage('ERR_UNEXPECTED'));
      }
    }
  };

  return (
    <ThemeProvider theme={customDialogImportTheme}>
      <Dialog maxWidth="lg" fullWidth open={open}>
        <Box className="modal_header_wrapper">
          <DialogTitle className="modal_header_title">
            {t('title.importMappingFile', {
              dataType:
                from === 'mappingCulture'
                  ? t('common.cultures').toLowerCase()
                  : t('navigation.groundTypes').toLowerCase(),
              mappingType: selectedMappingType,
            })}
          </DialogTitle>
          <ClearIcon
            className="modal_header_close_icon"
            onClick={() => {
              setModalOpen(false);
              setFile(null);
            }}
          />
        </Box>
        <DialogContent>
          <div>{t('message.importWillOverwrite', { type: selectedMappingType })}</div>
          <Box className="modal_main_content">
            <ThemeProvider theme={customFileButtonTheme}>
              <Button component="label" startIcon={<CloudUpload />}>
                {file ? t('button.chooseAnotherFile') : t('button.chooseFile')}
                <input
                  type="file"
                  onChange={(e) => setFile(e?.target?.files?.[0] || null)}
                  accept="text/csv"
                  id="file"
                  hidden
                />
              </Button>
            </ThemeProvider>
            {file && (
              <Box className="file_info_wrapper">
                <div className="file_info">
                  <label htmlFor="file_name">{t('label.fileName')} :</label>
                  <span id="file_name"> {file?.name}</span>
                </div>
                <div className="file_info">
                  <label htmlFor="file_size">{t('label.fileSize')} :</label>
                  <span id="file_size"> {formatFileSize(file?.size)}</span>
                </div>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <ThemeProvider theme={customButtonTheme}>
            <Button
              id="inputLaunchImport"
              disabled={file === null}
              onClick={importMappingDataFromFile}
            >
              {t('button.launchImport')}
            </Button>
          </ThemeProvider>
          <ThemeProvider theme={customRedButtonTheme}>
            <Button
              id="inputCancelImport"
              onClick={() => {
                setModalOpen(false);
                setFile(null);
              }}
            >
              {t('common.cancel')}
            </Button>
          </ThemeProvider>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default ImportMappingFileModal;
